import React, { useState, useEffect } from 'react';
import { Link, useHistory } from "react-router-dom";
import Axios from 'axios';
import './Registration.css';
import { host } from '../App';
import {
  UserOutlined, LogoutOutlined, PlusCircleOutlined, SaveOutlined,
  CalendarOutlined, TeamOutlined, ShopOutlined, ToolOutlined,
  NumberOutlined, UserSwitchOutlined, FileTextOutlined,
  BarChartOutlined, LineChartOutlined, HomeOutlined,
  ProductOutlined, DollarOutlined, CheckCircleOutlined,
  SyncOutlined, DatabaseOutlined, SettingOutlined
} from '@ant-design/icons';
import {
  Button, Menu, Dropdown, Input, Select, DatePicker,
  Card, Row, Col, Form, Radio, message, Alert,
  Statistic, Progress, Tag, Divider, Modal, Typography
} from 'antd';
import dayjs from 'dayjs';

const { TextArea } = Input;
const { Option } = Select;
const { Title, Text } = Typography;

function MorningRegistration() {
  const history = useHistory();
  const token = localStorage.getItem("acessToken");
  const user = JSON.parse(localStorage.getItem("Name")) || "User";
  const [form] = Form.useForm();

  // State management
  const [shift, setShift] = useState("Morning Shift");
  const [date, setDate] = useState(dayjs());
  const [area, setArea] = useState("Others");
  const [entries, setEntries] = useState([
    {
      id: 1,
      operator: "",
      partInternalNo: "",
      customer: "",
      partNo: "",
      partDescription: "",
      target: "",
      workStation: "",
      produced: "",
      scrap: "",
      operator2: "",
      supervisorNotes: "",
      status: "pending"
    }
  ]);
  const [additionalRows, setAdditionalRows] = useState([]);
  const [morningOperators, setMorningOperators] = useState([]);
  const [nightOperators, setNightOperators] = useState([]);
  const [machineList, setMachineList] = useState([]);
  const [partDataset, setPartDataset] = useState([]);
  const [filteredParts, setFilteredParts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [submissionStatus, setSubmissionStatus] = useState(null);
  const [totalTarget, setTotalTarget] = useState(0);
  const [totalProduced, setTotalProduced] = useState(0);
  const [efficiency, setEfficiency] = useState(0);
  const [showStats, setShowStats] = useState(true);
  const [selectedMachine, setSelectedMachine] = useState("");

  // Stats for dashboard
  const [stats, setStats] = useState({
    todayEntries: 0,
    completedToday: 0,
    efficiency: 0,
    scrapRate: 0
  });

  // Initialize with 4 default rows
  useEffect(() => {
    const defaultEntries = [];
    for (let i = 1; i <= 4; i++) {
      defaultEntries.push({
        id: i,
        operator: "",
        partInternalNo: "",
        customer: "",
        partNo: "",
        partDescription: "",
        target: "",
        workStation: "",
        produced: "",
        scrap: "",
        operator2: "",
        supervisorNotes: "",
        status: "pending"
      });
    }
    setEntries(defaultEntries);
  }, []);

  // Calculate totals and efficiency
  useEffect(() => {
    const allRows = [...entries, ...additionalRows];
    const totalT = allRows.reduce((sum, row) => sum + (parseInt(row.target) || 0), 0);
    const totalP = allRows.reduce((sum, row) => sum + (parseInt(row.produced) || 0), 0);
    const eff = totalT > 0 ? Math.round((totalP / totalT) * 100) : 0;

    setTotalTarget(totalT);
    setTotalProduced(totalP);
    setEfficiency(eff);

    // Update stats
    setStats(prev => ({
      ...prev,
      todayEntries: allRows.length,
      completedToday: allRows.filter(row => row.target && row.produced).length,
      efficiency: eff
    }));
  }, [entries, additionalRows]);

  // Fetch initial data
  useEffect(() => {
    fetchInitialData();
    fetchTodayStats();
  }, []);

  const fetchInitialData = async () => {
    try {
      const [morningRes, nightRes, machinesRes, partsRes] = await Promise.all([
        Axios.get(`https://${host}/realapi/get/MorningOperators/${token}`),
        Axios.get(`https://${host}/realapi/get/NightOperators/${token}`),
        Axios.get(`https://${host}/realapi/get/Machines/${token}`),
        Axios.get(`https://${host}/realapi/get/part_dataset/${token}`)
      ]);
      setMorningOperators(morningRes.data);
      setNightOperators(nightRes.data);
      setMachineList(machinesRes.data);
      setPartDataset(partsRes.data);
      setFilteredParts(partsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      message.error('Failed to load initial data');
    }
  };

  const fetchTodayStats = async () => {
    try {
      const response = await Axios.get(`https://${host}/today-stats`, {
        params: { token, date: dayjs().format('YYYY-MM-DD') }
      });
      if (response.data) {
        setStats(prev => ({
          ...prev,
          ...response.data
        }));
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleAreaChange = (value) => {
    setArea(value);
    setIsLoading(true);

    const fetchAreaData = async () => {
      try {
        let partsUrl, machinesUrl;

        switch (value) {
          case "Rigid":
            partsUrl = `Rigidpart_dataset`;
            machinesUrl = `RigidMachines`;
            break;
          case "Flex":
            partsUrl = `Flexpart_dataset`;
            machinesUrl = `FlexMachines`;
            break;
          default:
            partsUrl = `part_dataset`;
            machinesUrl = `Machines`;
        }

        const [partsRes, machinesRes] = await Promise.all([
          Axios.get(`https://${host}/realapi/get/${partsUrl}/${token}`),
          Axios.get(`https://${host}/realapi/get/${machinesUrl}/${token}`)
        ]);

        setFilteredParts(partsRes.data);
        setMachineList(machinesRes.data);
      } catch (error) {
        console.error('Error fetching area data:', error);
        message.error(`Failed to load ${value} data`);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAreaData();
  };

  const handleShiftChange = (e) => {
    const selectedShift = e.target.value;
    setShift(selectedShift);
  };

  const handlePartSelect = (value, index) => {
    const selectedPart = filteredParts.find(part => part.Part_internalNo === value);
    if (selectedPart) {
      const updatedEntries = [...entries];
      updatedEntries[index] = {
        ...updatedEntries[index],
        partInternalNo: value,
        customer: selectedPart.Customer || "",
        partNo: selectedPart.PartNo || "",
        partDescription: selectedPart.Part_description || ""
      };
      setEntries(updatedEntries);
    }
  };

  const handleInputChange = (field, value, index) => {
    const updatedEntries = [...entries];
    updatedEntries[index] = {
      ...updatedEntries[index],
      [field]: value
    };

    // Auto-fill produced with target when target changes
    if (field === 'target') {
      updatedEntries[index].produced = value;
      updatedEntries[index].status = value ? "in-progress" : "pending";
    }

    // Update status based on completion
    if (field === 'produced' && value && updatedEntries[index].target) {
      updatedEntries[index].status = "completed";
    }

    setEntries(updatedEntries);
  };

  const addRow = () => {
    const newRow = {
      id: entries.length + additionalRows.length + 1,
      operator: entries[0]?.operator || "",
      partInternalNo: "",
      customer: "",
      partNo: "",
      partDescription: "",
      target: "",
      workStation: selectedMachine || entries[0]?.workStation || "",
      produced: "",
      scrap: "",
      operator2: "",
      supervisorNotes: "",
      status: "pending"
    };
    setAdditionalRows([...additionalRows, newRow]);
    message.success('New row added successfully');
  };

  const removeRow = (id) => {
    Modal.confirm({
      title: 'Remove Row',
      content: 'Are you sure you want to remove this row?',
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk() {
        setAdditionalRows(additionalRows.filter(row => row.id !== id));
        message.info('Row removed');
      }
    });
  };

  const clearForm = () => {
    Modal.confirm({
      title: 'Clear Form',
      content: 'Are you sure you want to clear all entries?',
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk() {
        setEntries([{
          id: 1,
          operator: "",
          partInternalNo: "",
          customer: "",
          partNo: "",
          partDescription: "",
          target: "",
          workStation: "",
          produced: "",
          scrap: "",
          operator2: "",
          supervisorNotes: "",
          status: "pending"
        }]);
        setAdditionalRows([]);
        message.success('Form cleared');
      }
    });
  };

  const handleLogout = () => {
    Modal.confirm({
      title: 'Logout',
      content: 'Are you sure you want to logout?',
      okText: 'Yes',
      okType: 'primary',
      cancelText: 'No',
      onOk() {
        localStorage.clear();
        sessionStorage.clear();
        history.push('/');
      }
    });
  };

  const userMenu = (
    <Menu>
      <Menu.Item key="dashboard" icon={<HomeOutlined />}>
        <Link to="/dashboard">Dashboard</Link>
      </Menu.Item>
      <Menu.Item key="reports" icon={<BarChartOutlined />}>
        <Link to="/reports">Reports</Link>
      </Menu.Item>
      <Menu.Divider />
      <Menu.Item key="logout" icon={<LogoutOutlined />} danger>
        <a onClick={handleLogout}>Logout</a>
      </Menu.Item>
    </Menu>
  );

  const validateForm = () => {
    const allRows = [...entries, ...additionalRows];
    const validRows = allRows.filter(row =>
      row.operator && row.partInternalNo && row.target && row.workStation
    );

    if (validRows.length === 0) {
      message.error('Please fill at least one complete row with all required fields');
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsLoading(true);
    setSubmissionStatus(null);

    try {
      const allRows = [...entries, ...additionalRows];
      const validRows = allRows.filter(row =>
        row.operator && row.partInternalNo && row.target && row.workStation
      );

      const submissionData = validRows.map(row => ({
        Date: date.format('YYYY-MM-DD'),
        Operator: row.operator,
        Part_internalNo: row.partInternalNo,
        PartNo: row.partNo,
        Customer: row.customer,
        Part_description: row.partDescription,
        Target: row.target,
        work_station: row.workStation,
        Produced: row.produced || row.target,
        Scrap: row.scrap || 0,
        Supervisor_notes: row.supervisorNotes,
        Operator_2: row.operator2,
        token: token
      }));

      const promises = submissionData.map(data =>
        Axios.post(`https://${host}/realapi/create`, data)
      );

      await Promise.all(promises);

      setSubmissionStatus('success');
      message.success(`${validRows.length} entries submitted successfully!`);

      // Update stats after successful submission
      fetchTodayStats();

    } catch (error) {
      console.error('Error submitting data:', error);
      setSubmissionStatus('error');
      message.error('Failed to submit data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const currentOperators = shift === "Morning Shift" ? morningOperators : nightOperators;

  return (
    <div className="registration-page">
      {/* Header */}
      <header className="registration-header">
        <div className="header-gradient">
          <div className="header-content">
            <div className="header-left">
              <Title level={2} className="header-title">
                <DatabaseOutlined /> Production Data Entry
              </Title>
              <div className="header-subtitle">
                <Tag color="green" icon={<TeamOutlined />}>{shift}</Tag>
                <Tag color="blue" icon={<CalendarOutlined />}>{date.format('DD MMM YYYY')}</Tag>
                <Tag color="orange" icon={<ShopOutlined />}>{area} Area</Tag>
              </div>
            </div>

            <div className="header-right">
              <Dropdown overlay={userMenu} trigger={['click']}>
                <Button
                  icon={<UserOutlined />}
                  size="large"
                  className="user-profile-btn"
                >
                  <span className="user-name">{user.replace(/[{""}]/g, "")}</span>
                </Button>
              </Dropdown>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="registration-content">
        {/* Top Stats Card */}
        <div className="stats-overview">
          <Card className="stats-card" hoverable>
            <Row gutter={[16, 16]}>
              <Col xs={12} sm={6}>
                <Statistic
                  title="Today's Entries"
                  value={stats.todayEntries + entries.length + additionalRows.length}
                  prefix={<FileTextOutlined />}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Col>
              <Col xs={12} sm={6}>
                <Statistic
                  title="Completed"
                  value={stats.completedToday}
                  prefix={<CheckCircleOutlined />}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Col>
              <Col xs={12} sm={6}>
                <Statistic
                  title="Efficiency"
                  value={efficiency}
                  suffix="%"
                  prefix={<SyncOutlined />}
                  valueStyle={{ color: '#faad14' }}
                />
                <Progress percent={efficiency} size="small" status="active" />
              </Col>
              <Col xs={12} sm={6}>
                <Statistic
                  title="Total Target"
                  value={totalTarget}
                  prefix={<DollarOutlined />}
                  valueStyle={{ color: '#722ed1' }}
                />
              </Col>
            </Row>
          </Card>
        </div>

        {/* Control Panel */}
        <Card className="control-panel" title={<span><SettingOutlined /> Production Controls</span>}>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={4}>
              <div className="control-group">
                <label className="control-label">Shift</label>
                <Radio.Group
                  value={shift}
                  onChange={handleShiftChange}
                  className="shift-control"
                  buttonStyle="solid"
                >
                  <Radio.Button value="Morning Shift">☀️ Morning</Radio.Button>
                  <Radio.Button value="Night Shift">🌙 Night</Radio.Button>
                </Radio.Group>
              </div>
            </Col>

            <Col xs={24} sm={12} md={4}>
              <div className="control-group">
                <label className="control-label">Date</label>
                <DatePicker
                  value={date}
                  onChange={setDate}
                  format="DD MMM YYYY"
                  className="date-control"
                  suffixIcon={<CalendarOutlined />}
                />
              </div>
            </Col>

            <Col xs={24} sm={12} md={4}>
              <div className="control-group">
                <label className="control-label">Area</label>
                <Select
                  value={area}
                  onChange={handleAreaChange}
                  className="area-control"
                  suffixIcon={<ShopOutlined />}
                >
                  <Option value="Others">🏭 Others</Option>
                  <Option value="Flex">🔄 Flex</Option>
                  <Option value="Rigid">🛠️ Rigid</Option>
                </Select>
              </div>
            </Col>

            <Col xs={24} sm={12} md={4}>
              <div className="control-group">
                <label className="control-label">Default Machine</label>
                <Select
                  value={selectedMachine}
                  onChange={setSelectedMachine}
                  className="machine-control"
                  placeholder="Select Machine"
                  suffixIcon={<ToolOutlined />}
                >
                  <Option value="">All Machines</Option>
                  {machineList.map(machine => (
                    <Option key={machine.Machines} value={machine.Machines}>
                      {machine.Machines}
                    </Option>
                  ))}
                </Select>
              </div>
            </Col>

            <Col xs={24} sm={12} md={4}>
              <div className="control-group">
                <label className="control-label">Quick Actions</label>
                <div className="quick-actions-buttons">
                  <Button
                    type="primary"
                    icon={<PlusCircleOutlined />}
                    onClick={addRow}
                    block
                  >
                    Add Row
                  </Button>
                </div>
              </div>
            </Col>

            <Col xs={24} sm={12} md={4}>
              <div className="control-group">
                <label className="control-label">Navigation</label>
                <div className="nav-buttons">
                  <Link to="/openorder">
                    <Button icon={<FileTextOutlined />} block>Open Orders</Button>
                  </Link>
                  <Link to="/graph">
                    <Button icon={<LineChartOutlined />} block>Charts</Button>
                  </Link>
                </div>
              </div>
            </Col>
          </Row>
        </Card>

        {/* Submission Status */}
        {submissionStatus && (
          <Alert
            message={submissionStatus === 'success' ? 'Success' : 'Error'}
            description={
              submissionStatus === 'success'
                ? 'Data has been submitted successfully!'
                : 'There was an error submitting the data. Please try again.'
            }
            type={submissionStatus}
            showIcon
            closable
            className="status-alert"
          />
        )}

        {/* Data Entry Section */}
        <div className="data-entry-section">
          <Card
            title={
              <div className="section-title">
                <ProductOutlined /> Production Entries
                <div className="section-stats">
                  <Tag color="blue">{entries.length + additionalRows.length} Rows</Tag>
                  <Tag color="green">{totalTarget} Target</Tag>
                  <Tag color="orange">{efficiency}% Efficiency</Tag>
                </div>
              </div>
            }
            className="data-entry-card"
            extra={
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={handleSubmit}
                loading={isLoading}
                size="large"
                className="submit-all-btn"
              >
                Submit All Data
              </Button>
            }
          >
            {/* Main Entries */}
            <div className="entries-grid">
              {entries.map((entry, index) => (
                <EntryRow
                  key={entry.id}
                  index={index}
                  entry={entry}
                  operators={currentOperators}
                  parts={filteredParts}
                  machines={machineList}
                  isLoading={isLoading}
                  onPartSelect={handlePartSelect}
                  onInputChange={handleInputChange}
                  selectedMachine={selectedMachine}
                  isMainRow={true}
                />
              ))}

              {/* Additional Rows */}
              {additionalRows.map((row, index) => (
                <EntryRow
                  key={row.id}
                  index={entries.length + index}
                  entry={row}
                  operators={currentOperators}
                  parts={filteredParts}
                  machines={machineList}
                  isLoading={isLoading}
                  onPartSelect={handlePartSelect}
                  onInputChange={handleInputChange}
                  selectedMachine={selectedMachine}
                  onRemove={() => removeRow(row.id)}
                  isAdditional={true}
                />
              ))}
            </div>

            {/* Row Actions */}
            <Divider />
            <div className="row-actions">
              <Button
                type="dashed"
                icon={<PlusCircleOutlined />}
                onClick={addRow}
                size="large"
                className="add-more-btn"
              >
                Add More Rows
              </Button>

              <Button
                type="default"
                danger
                onClick={clearForm}
                className="clear-btn"
              >
                Clear All
              </Button>
            </div>

            {/* Summary Section */}
            <div className="summary-section">
              <Card size="small" className="summary-card">
                <Row gutter={[16, 16]}>
                  <Col span={12}>
                    <div className="summary-item">
                      <Text type="secondary">Total Rows:</Text>
                      <Title level={4}>{entries.length + additionalRows.length}</Title>
                    </div>
                  </Col>
                  <Col span={12}>
                    <div className="summary-item">
                      <Text type="secondary">Total Target:</Text>
                      <Title level={4} style={{ color: '#1890ff' }}>{totalTarget}</Title>
                    </div>
                  </Col>
                  <Col span={12}>
                    <div className="summary-item">
                      <Text type="secondary">Total Produced:</Text>
                      <Title level={4} style={{ color: '#52c41a' }}>{totalProduced}</Title>
                    </div>
                  </Col>
                  <Col span={12}>
                    <div className="summary-item">
                      <Text type="secondary">Efficiency:</Text>
                      <Title level={4} style={{ color: efficiency > 80 ? '#52c41a' : '#faad14' }}>
                        {efficiency}%
                      </Title>
                    </div>
                  </Col>
                </Row>
              </Card>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

// Entry Row Component
const EntryRow = ({
  index,
  entry,
  operators,
  parts,
  machines,
  isLoading,
  onPartSelect,
  onInputChange,
  selectedMachine,
  onRemove,
  isAdditional = false,
  isMainRow = false
}) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'in-progress': return 'processing';
      default: return 'default';
    }
  };

  return (
    <div className={`entry-row-card ${isAdditional ? 'additional-row' : ''} ${isMainRow ? 'main-row' : ''}`}>
      <div className="row-header">
        <div className="row-info">
          <span className="row-number">Entry #{index + 1}</span>
          <Tag color={getStatusColor(entry.status)} className="status-tag">
            {entry.status === 'completed' ? '✓ Completed' :
              entry.status === 'in-progress' ? '⏳ In Progress' : '⏳ Pending'}
          </Tag>
        </div>
        {isAdditional && (
          <Button
            type="text"
            icon={<i className="fas fa-times"></i>}
            onClick={onRemove}
            className="remove-btn"
            danger
            size="small"
          />
        )}
      </div>

      <Row gutter={[12, 12]} className="entry-fields">
        {/* Operator */}
        <Col xs={24} sm={12} md={6} lg={4}>
          <div className="form-field">
            <label className="field-label">
              <TeamOutlined /> Operator *
            </label>
            <Select
              placeholder="Select Operator"
              value={entry.operator}
              onChange={(value) => onInputChange('operator', value, index)}
              showSearch
              optionFilterProp="children"
              className="field-input operator-select"
              loading={isLoading}
            >
              {operators.map(op => (
                <Option key={op.Operators} value={op.Operators}>
                  <div className="operator-option">
                    <span className="operator-name">{op.Operators}</span>
                  </div>
                </Option>
              ))}
            </Select>
          </div>
        </Col>

        {/* Internal Part Number */}
        <Col xs={24} sm={12} md={6} lg={4}>
          <div className="form-field">
            <label className="field-label">
              <NumberOutlined /> Internal Part # *
            </label>
            <Select
              placeholder="Search parts..."
              value={entry.partInternalNo}
              onChange={(value) => onPartSelect(value, index)}
              showSearch
              loading={isLoading}
              optionFilterProp="children"
              className="field-input part-select"
            >
              {parts.map(part => (
                <Option key={part.Part_internalNo} value={part.Part_internalNo}>
                  <div className="part-option">
                    <span className="part-number">{part.Part_internalNo}</span>
                    <span className="part-description">{part.Part_description}</span>
                  </div>
                </Option>
              ))}
            </Select>
          </div>
        </Col>

        {/* Customer & Part Info */}
        <Col xs={24} sm={12} md={6} lg={4}>
          <div className="form-field">
            <label className="field-label">Customer</label>
            <Input
              value={entry.customer}
              placeholder="Auto-filled"
              readOnly
              className="field-input customer-input"
            />
          </div>
        </Col>

        <Col xs={24} sm={12} md={6} lg={3}>
          <div className="form-field">
            <label className="field-label">Part No</label>
            <Input
              value={entry.partNo}
              placeholder="Auto-filled"
              readOnly
              className="field-input partno-input"
            />
          </div>
        </Col>

        {/* Target & Production */}
        <Col xs={12} sm={6} md={4} lg={3}>
          <div className="form-field">
            <label className="field-label">Target *</label>
            <Input
              type="number"
              value={entry.target}
              onChange={(e) => onInputChange('target', e.target.value, index)}
              placeholder="Set target"
              className="field-input target-input"
              min="0"
              suffix="units"
            />
          </div>
        </Col>

        <Col xs={12} sm={6} md={4} lg={3}>
          <div className="form-field">
            <label className="field-label">Produced</label>
            <Input
              type="number"
              value={entry.produced || entry.target}
              onChange={(e) => onInputChange('produced', e.target.value, index)}
              placeholder="Produced"
              className="field-input produced-input"
              min="0"
              suffix="units"
            />
          </div>
        </Col>

        {/* Work Station */}
        <Col xs={12} sm={6} md={4} lg={3}>
          <div className="form-field">
            <label className="field-label">
              <ToolOutlined /> Work Station *
            </label>
            <Select
              placeholder="Select Station"
              value={entry.workStation || selectedMachine}
              onChange={(value) => onInputChange('workStation', value, index)}
              className="field-input station-select"
            >
              {machines.map(machine => (
                <Option key={machine.Machines} value={machine.Machines}>
                  {machine.Machines}
                </Option>
              ))}
            </Select>
          </div>
        </Col>

        {/* Scrap & Operator 2 */}
        <Col xs={12} sm={6} md={4} lg={2}>
          <div className="form-field">
            <label className="field-label">Scrap</label>
            <Input
              type="number"
              value={entry.scrap}
              onChange={(e) => onInputChange('scrap', e.target.value, index)}
              placeholder="0"
              className="field-input scrap-input"
              min="0"
            />
          </div>
        </Col>

        <Col xs={12} sm={6} md={4} lg={3}>
          <div className="form-field">
            <label className="field-label">
              <UserSwitchOutlined /> Operator 2
            </label>
            <Select
              placeholder="Optional"
              value={entry.operator2}
              onChange={(value) => onInputChange('operator2', value, index)}
              className="field-input operator2-select"
            >
              <Option value="">None</Option>
              {operators.map(op => (
                <Option key={op.Operators} value={op.Operators}>
                  {op.Operators}
                </Option>
              ))}
            </Select>
          </div>
        </Col>
      </Row>

      {/* Part Description and Notes */}
      <div className="row-bottom-section">
        <Col span={12}>
          <div className="form-field">
            <label className="field-label">Part Description</label>
            <Input.TextArea
              value={entry.partDescription}
              placeholder="Part description will appear here..."
              readOnly
              rows={2}
              className="description-textarea"
            />
          </div>
        </Col>

        <Col span={12}>
          <div className="form-field">
            <label className="field-label">Supervisor Notes</label>
            <Input.TextArea
              value={entry.supervisorNotes}
              onChange={(e) => onInputChange('supervisorNotes', e.target.value, index)}
              placeholder="Add notes here..."
              rows={2}
              className="notes-textarea"
            />
          </div>
        </Col>
      </div>
    </div>
  );
};

export default MorningRegistration;
